import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Estimate, EstimateVersion } from "../../models/estimate.model";
import { EstimatesService } from "../../services/estimates.service";
import { FormControl } from "@angular/forms";
import { Subject, Subscription, timer } from "rxjs";
import { debounceTime, tap } from "rxjs/operators";
import { isEmpty, decimalOnly, onDecimalPaste } from "../../helpers/forms";
import { MatLegacySnackBar as MatSnackBar } from "@angular/material/legacy-snack-bar";
import { showSnackbar } from "projects/common/src/lib/components/snackbar/snackbar.component";
import { surchargeBannedStates } from "../../services/invoices.service";

type EstimateSummaryValues = {
  subtotal: number,
  discountDisplay: string,
  salesTax: number,
  total: number
};

@Component({
  selector: 'app-estimate-summary',
  templateUrl: './estimate-summary.component.html',
  styleUrls: ['./estimate-summary.component.scss']
})
export class EstimateSummaryComponent implements OnInit, OnChanges, OnDestroy {

  protected readonly isNaN = isNaN;
  protected readonly decimalOnly = decimalOnly;
  protected readonly onPaste = onDecimalPaste;

  @Input() readonly = false;

  @Output() depositFocus = new EventEmitter<boolean>();

  showingDepositSnackbar = false;

  estimateSummaryValues: EstimateSummaryValues = {
    subtotal: 0,
    discountDisplay: '',
    salesTax: 0,
    total: 0
  };

  displayDeposit: number = 0;

  depositFocus$ = new Subject<boolean>();

  depositPercentFormControl = new FormControl();
  depositAmountFormControl = new FormControl();

  depositPercentFormControlSubscription!: Subscription;
  depositAmountFormControlSubscription!: Subscription;
  depositFocusSubscription!: Subscription;
  depositRecalculateSubscription!: Subscription;

  depositAbove: boolean = false;

  showDisplayDeposit = true;

  currentDepositAmount: number | null | undefined;
  currentDepositPercent: number | null | undefined;
  currentDiscountType?: 'amount' | 'percent';
  typeChange = false;

  @Input() estimate!: Estimate;
  @Input() estimateVersion!: EstimateVersion;

  surchargeError: string | false = false;

  private surchargeErrorTimerSub?: Subscription;

  constructor(
    private estimatesService: EstimatesService,
    private snackbar: MatSnackBar
  ) {
    this.depositRecalculateSubscription = this.estimatesService.recalculate$.pipe(
      tap(_ => {
        this.setDeposit(false, true).then()
      })
    ).subscribe();

    this.depositPercentFormControlSubscription = this.depositPercentFormControl.valueChanges.subscribe(
      percent => this.reCalcDisplayDeposit(percent, 'percent')
    );

    this.depositAmountFormControlSubscription = this.depositAmountFormControl.valueChanges.subscribe(
      amount => this.reCalcDisplayDeposit(amount, 'amount')
    );

    this.depositFocusSubscription = this.depositFocus$.pipe(
      debounceTime(150),
      tap(async focus => {
        await this.setDeposit(focus)
      })
    ).subscribe();
  }

  async setDeposit(focus: boolean, noSnackBar?: boolean) {
    if (!focus) {
      if (this.estimateVersion.depositType === 'percent') {
        this.estimateVersion.depositPercent = +this.depositPercentFormControl.value;
        this.estimateVersion.depositAmount = parseFloat((+this.displayDeposit).toFixed(2));
      } else if (this.estimateVersion.depositType === 'amount') {
        this.estimateVersion.depositAmount = +this.depositAmountFormControl.value;
        this.estimateVersion.depositPercent = parseFloat((+this.displayDeposit).toFixed(2));
      }

      if (
        this.displayDeposit === 0
        && this.estimateVersion.depositAmount === 0
        && this.estimateVersion.depositPercent === 0
      ) {
        await this.saveDeposit(noSnackBar);
      } else if (
        this.estimateVersion.depositAmount
        && !isNaN(this.estimateVersion.depositAmount)
        && this.estimateVersion.depositPercent
        && !isNaN(this.estimateVersion.depositPercent)
      ) {
        this.showDisplayDeposit = !!noSnackBar;
        await this.saveDeposit(noSnackBar);
        setTimeout((() => {
          this.showDisplayDeposit = true;
        }).bind(this), 250);
      }
    } else {
      this.currentDepositAmount = this.estimateVersion.depositAmount;
      this.currentDepositPercent = this.estimateVersion.depositPercent;
    }
  }

  private async saveDeposit(noSnackBar?: boolean) {
    let empty = false;
    if (
      (isEmpty(this.currentDepositAmount) && isEmpty(this.estimateVersion.depositAmount))
      || (isEmpty(this.currentDepositPercent) && isEmpty(this.estimateVersion.depositPercent))
    )
      empty = true;
    if (
      this.currentDepositAmount !== this.estimateVersion.depositAmount
      || this.currentDepositPercent !== this.estimateVersion.depositPercent
      || this.typeChange
    ) {
      this.typeChange = false;
      this.depositAmountFormControl.disable();
      this.depositPercentFormControl.disable();
      await this.saveEstimateVersion();
      if (
        !this.showingDepositSnackbar
        && !noSnackBar
        && !empty
      ) {
        showSnackbar(this.snackbar, {
          message: 'Deposit set',
          duration: 2000,
        });
        this.showingDepositSnackbar = true;
        setTimeout(() => {
          this.showingDepositSnackbar = false;
        }, 1000);
      }
      this.depositAmountFormControl.enable();
      this.depositPercentFormControl.enable();
    }
  }

  ngOnInit() {
    if (this.estimateVersion.depositType === 'percent' && this.estimateVersion.depositPercent) {
      const fraction = this.estimateVersion.depositPercent
        ?.toFixed(2).toString().split('.')[1]?.substring(0, 2);
      this.depositPercentFormControl.patchValue(
        fraction === '00'
          ? this.estimateVersion.depositPercent?.toFixed(0)
          : this.estimateVersion.depositPercent?.toFixed(2)
      );
    } else if (this.estimateVersion.depositType === 'amount' && this.estimateVersion.depositAmount) {
      this.depositAmountFormControl.patchValue(this.estimateVersion.depositAmount?.toFixed(2));
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if ('estimateVersion' in changes) {
      this.currentDiscountType = this.estimateVersion.discountType;
      this.calculateValues();
      if (this.estimateVersion.depositType === 'amount')
        this.reCalcDisplayDeposit(this.depositAmountFormControl.value, 'amount');
      else if (this.estimateVersion.depositType === 'percent')
        this.reCalcDisplayDeposit(this.depositPercentFormControl.value, 'percent');
    }
  }

  ngOnDestroy() {
    this.depositPercentFormControlSubscription?.unsubscribe();
    this.depositAmountFormControlSubscription?.unsubscribe();
    this.depositFocusSubscription?.unsubscribe();
    this.depositRecalculateSubscription?.unsubscribe();
  }

  calculateValues() {
    this.estimateSummaryValues.subtotal = 0;
    this.estimateSummaryValues.salesTax = 0;
    this.estimateSummaryValues.total = 0;
    let totalDiscountAmount = 0;

    this.estimateVersion.items?.forEach(item => {
      let discounted = 0;
      let taxed = 0;

      this.estimateSummaryValues.subtotal += (item.quantity ?? 0) * item.price;
      totalDiscountAmount += (item.discountAmount ?? 0) * (item.quantity ?? 0);

      discounted = item.price - (item.discountAmount ?? 0);

      taxed = !item.taxable
        ? discounted
        : discounted + (discounted * (this.estimateVersion.clientSalesTaxPercentage ?? 0) / 100);

      if (item.quantity && item.taxable && item.price)
        this.estimateSummaryValues.salesTax += parseFloat(((taxed - discounted) * item.quantity).toFixed(2));

      if (item.quantity)
        this.estimateSummaryValues.total += parseFloat((taxed * item.quantity).toFixed(2));
    });

    if (this.currentDiscountType === 'amount')
      this.estimateSummaryValues.discountDisplay = totalDiscountAmount.toFixed(2);
    else {
      this.estimateSummaryValues.discountDisplay
        = ((totalDiscountAmount/this.estimateSummaryValues.subtotal)*100).toFixed(2);
      if (this.estimateSummaryValues.discountDisplay === 'Infinity')
        this.estimateSummaryValues.discountDisplay = '0';
      if (this.estimateSummaryValues.discountDisplay.split('.')[1]?.substring(0, 2) === '00')
        this.estimateSummaryValues.discountDisplay = this.estimateSummaryValues.discountDisplay.slice(0, -3);
    }

    this.estimateSummaryValues.total =
      this.estimateSummaryValues.subtotal
      - totalDiscountAmount
      + this.estimateSummaryValues.salesTax;

  }

  async toggleSignature(status: boolean) {
    this.estimateVersion.signature = status;
    await this.saveEstimateVersion();
  }

  async toggleDiscountType(type: 'amount' | 'percent') {
    if (
        !this.depositAmountFormControl.disabled
        && !this.depositPercentFormControl.disabled
    ) {
      if (type !== this.estimateVersion.discountType) {
        this.currentDiscountType = type;
        this.calculateValues();
        this.estimateVersion.discountType = type;
        await this.saveEstimateVersion();
      }
    }
  }

  async toggleDepositType() {
    this.typeChange = true;
    if (!this.displayDeposit || this.displayDeposit === 0) {
      this.depositAmountFormControl.patchValue(null);
      this.depositPercentFormControl.patchValue(null);
    }
    if (this.estimateVersion.depositType === 'percent') {
      if (+this.displayDeposit === Infinity || isNaN(+this.displayDeposit))
        this.depositAmountFormControl.patchValue(0);
      else if (this.displayDeposit !== 0)
        this.depositAmountFormControl.patchValue(+this.displayDeposit.toFixed(2));
      this.estimateVersion.depositType = 'amount';
    } else if (this.estimateVersion.depositType === 'amount') {
      if (+this.displayDeposit === Infinity || isNaN(+this.displayDeposit))
        this.depositPercentFormControl.patchValue(0);
      else if (this.displayDeposit !== 0)
        this.depositPercentFormControl.patchValue(+this.displayDeposit.toFixed(2));
      this.estimateVersion.depositType = 'percent';
    }
  }

  private reCalcDisplayDeposit(value: any, which: 'percent' | 'amount') {
    if (isEmpty(value)) {
      this.displayDeposit = 0;
      this.depositAbove = false;
    } else {
      if (value && !isNaN(value)) {
        this.depositAbove = false;

        if (which === 'percent') {
          if (value > 100)
            this.depositAbove = true;
          this.displayDeposit = (value * this.estimateSummaryValues.total)/100;
        } else if (which === 'amount') {
          if (value > this.estimateSummaryValues.total)
            this.depositAbove = true;
          this.displayDeposit = (value/this.estimateSummaryValues.total)*100;
        }
      }
    }
  }

  formatDeposit(rawDeposit: number) {
    if (rawDeposit === Infinity || isNaN(rawDeposit)) {
      return '0';
    } else {
      if (this.estimateVersion.depositType === 'percent') {
        return rawDeposit.toFixed(2);
      } else if (this.estimateVersion.depositType === 'amount') {
        if (rawDeposit?.toString().split('.')[1]?.substring(0, 2) === '00')
          return this.displayDeposit.toFixed(2).slice(0, -3);
        else
          return rawDeposit.toFixed(2);
      } else return 0;
    }
  }

  async toggleSurcharge(event: any) {
    const clientState = this.estimateVersion.clientAddress?.state;
    if (clientState && surchargeBannedStates.includes(clientState.toLowerCase())) {
      event.source.checked = false;
      this.surchargeError = `Surcharge is not available in ${clientState}`;

      this.surchargeErrorTimerSub?.unsubscribe();
      this.surchargeErrorTimerSub = timer(2000).subscribe(() => this.surchargeError = false);
    } else {
      this.estimateVersion.surcharge = event.checked;
      await this.saveEstimateVersion();
    }
  }

  private async saveEstimateVersion() {
    await this.estimatesService.updateEstimateVersion(this.estimateVersion.id, {
      signature: this.estimateVersion.signature,
      surcharge: this.estimateVersion.surcharge,
      depositType: this.estimateVersion.depositType,
      depositAmount: this.estimateVersion.depositAmount,
      depositPercent: this.estimateVersion.depositPercent,
      discountType: this.estimateVersion.discountType
    });
  }
}
